/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { getGuid } from 'src/core/utils/randomValueGenerators';
import { DocumentStorageService } from 'src/customText/core/utils/documentStorage/types';

/**
 * Creates an Azure service that contains real network calls
 * to fetch documents from an Azure blob store.
 */
export const getAzureDocumentStorageService = ({
    tenantToken,
    containerUrl
}: {
    tenantToken: string;
    containerUrl: string;
}): DocumentStorageService => {
    const getRequestOptions = (token: string) => {
        const options = luisRequestOptionsBuilder()
            .useArmToken(token)
            .useFileContent('lu')
            .disableUserSessionId()
            .build();

        options.headers['x-ms-version'] = '2019-02-02';

        return options;
    };

    return {
        createDocument: async (content: string) => {
            const documentId = getGuid();
            const url = `${containerUrl}/${documentId}`;

            await httpService.put<void>(url, content, getRequestOptions(tenantToken));

            return documentId;
        },
        deleteDocument: async (documentId: string) => {
            const url = `${containerUrl}/${encodeURIComponent(documentId)}`;

            await httpService.delete(url, getRequestOptions(tenantToken));
        },
        getDocument: async (documentId: string) => {
            const url = `${containerUrl}/${encodeURIComponent(documentId)}`;
            const { data } = await httpService.download(url, getRequestOptions(tenantToken));
            return await fileService.readFromDisk(data);
        },
        updateDocument: async (documentId: string, content: string) => {
            const url = `${containerUrl}/${encodeURIComponent(documentId)}`;
            const requestOptions = getRequestOptions(tenantToken);
            requestOptions.headers['x-ms-blob-type'] = 'BlockBlob';

            await httpService.put<void>(url, content, requestOptions);
        }
    };
};
